import { InjectionToken } from '@angular/core';
import {
  BooleanMatchStrategy,
  DateMatchStrategy,
  NumberMatchStrategy,
  TextMatchStrategy,
} from './local-handling/filter/row.filter.interface';
import { RowInterface } from './row/row.component';
import type { TableComponent } from './table.component';

export interface TableColumnConfigInterface<T = any> {
  key: keyof T;
  label?: string;
  type?:
    | 'text'
    | 'number'
    | 'date'
    | 'datetime'
    | 'time'
    | 'currency'
    | 'percentage'
    | 'boolean'
    | 'icon'
    | 'image'
    | 'link'
    | 'routerLink'
    | 'links'
    | 'button'
    | 'chips'
    | 'capped-chips'
    | 'boolean-sync'
    | 'status'
    | 'count';

  href?: string;
  target?: '_self' | '_blank' | '_parent' | '_top' | '';
  routerLink?: string[];
  sortable?: boolean;
  filterOnBlur?: boolean;
  sortComparator?: (a: T, b: T) => number;
  filterable?: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  alwaysVisible?: boolean;
  visible?: boolean;
  clamp?: number;
  filterOptions?: { id: string | number; label: string }[];
  filterMatchStrategy?: TextMatchStrategy | BooleanMatchStrategy | NumberMatchStrategy | DateMatchStrategy; // only used for local handling
  filterApiKey?: string;
  hidden?: boolean;
  alwaysIncluded?: boolean;
  isFrontEndCalculated?: boolean;
  useSingleSelect?: boolean;
}

export interface TableFilterInterface<T = any> {
  key: keyof T;
  value: any;
}

export interface TablePaginationInterface {
  from: number;
  to: number;
  total: number;
}

export interface TableSortInterface<T = any> {
  key: keyof T;
  direction: 'asc' | 'desc' | undefined;
}

export interface TableRowActionInterface<T = any> {
  label: string;
  icon?: string;
  click: (row: T | T[]) => void;
  condition?: (row: T) => boolean;
}

export interface TableGroupActionInterface<T = any> {
  label: string;
  icon?: string;
  click: (group: T | T[], table?: TableComponent) => void;
  condition?: (group: T) => boolean;
}

export interface TableBulkActionInterface<T = any> {
  label: string;
  icon?: string;
  click: (group: T | T[], table?: TableComponent) => void;
  condition?: (rows: RowInterface<T>[]) => boolean;
}

export class TableSelectionChange<T = any> {
  constructor(
    public selected: T,
    public added: T,
    public removed: T
  ) {}
}

export class TableVisibleColumnsChange<T = any> {
  constructor(
    public selected: TableColumnConfigInterface<T>[],
    public added: TableColumnConfigInterface<T>[],
    public removed: TableColumnConfigInterface<T>[]
  ) {}
}

export class TableShowOnlySelectionChange<T = any> {
  constructor(
    public showOnlySelection: boolean,
    public selectedIds: number[],
    public columns: TableColumnConfigInterface[]
  ) {}
}

// Note: this interface also exists in GroupedData.interface.ts (@campus/dal)
export interface GroupedData<T> {
  [key: string]: any;
  open: boolean;
  children: T[];
  allChildren?: T[];
}

export interface TableRowParentInterface {
  multiple: boolean;
  readonly: boolean;
}

export const CDS_TABLE_ROW_PARENT = new InjectionToken<TableRowParentInterface>('cds-table-row-parent');
